import "./MesRdvClient.css";
import { useEffect, useState } from "react";
import { markNotificationScopeRead } from "../../utils/notificationsApi";

function MesRdvClient() {
  const [rdvs, setRdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cancelModal, setCancelModal] = useState({ visible: false, rdv: null });
  const [deleting, setDeleting] = useState(false);

  // Vue active : "upcoming" (à venir) | "past" (passés)
  const [activeTab, setActiveTab] = useState("upcoming");
  // Horloge interne pour reclasser automatiquement (toutes les 60 s)
  const [now, setNow] = useState(new Date());

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  useEffect(() => {
    markNotificationScopeRead("rdv");
  }, []);

  // Rafraîchit "now" chaque minute pour reclasser sans recharger
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const fetchRdvs = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/rdvs/client/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Erreur ${response.status} : ${errorText}`);
        }

        const data = await response.json();
        setRdvs(data);
      } catch (error) {
        setError("Impossible de charger les rendez-vous.");
      } finally {
        setLoading(false);
      }
    };

    fetchRdvs();
  }, [userId, token]);

  const reallyCancel = async (rdvId) => {
    try {
      setDeleting(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/rdvs/${rdvId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        alert("Erreur lors de la suppression du rendez-vous.");
        return;
      }

      setRdvs((prev) => prev.filter((rdv) => rdv._id !== rdvId));
      setCancelModal({ visible: false, rdv: null });
    } catch (error) {
      alert("Erreur de connexion ou serveur.");
    } finally {
      setDeleting(false);
    }
  };

  // ------- Helpers pour classer les RDVs -------
  const safeSplit = (str, sep) =>
    typeof str === "string" ? str.split(sep) : [];

  const parseFrDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const [d, m, y] = safeSplit(dateStr, "/").map((x) => parseInt(x, 10));
    const [hh = 0, mm = 0] = timeStr ? safeSplit(timeStr, ":").map((x) => parseInt(x, 10)) : [0, 0];
    if (!y || !m || !d) return null;
    return new Date(y, (m || 1) - 1, d, hh || 0, mm || 0, 0, 0);
  };

  const getStartEnd = (rdv) => {
    let start = rdv.start ? new Date(rdv.start) : null;
    if (!start) start = parseFrDateTime(rdv.date, rdv.heure) || null;
    let end = rdv.end ? new Date(rdv.end) : null;
    // Si pas d'end fourni, on considère 30 minutes par défaut
    if (!end && start) end = new Date(start.getTime() + 30 * 60000);
    return { start, end };
  };

  const isPast = (rdv) => {
    const { start, end } = getStartEnd(rdv);
    if (!start) return false; // si non daté, on ne le classe pas en passé
    if (end) return end.getTime() < now.getTime();
    return start.getTime() < now.getTime();
  };

  const upcomingRdvs = rdvs.filter((r) => !isPast(r));
  const pastRdvs = rdvs.filter((r) => isPast(r));

  const listToRender = activeTab === "upcoming" ? upcomingRdvs : pastRdvs;

  if (loading) return <p className="loading">Chargement...</p>;
  if (error) return <p className="loading">{error}</p>;

  // ------- Modal d'annulation (style popslot) -------
  const renderCancelPopup = () => {
    if (!cancelModal.visible || !cancelModal.rdv) return null;
    const rdv = cancelModal.rdv;

    const { start, end } = getStartEnd(rdv);
    const startTxt = start
      ? start.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
      : rdv.heure || "-";
    const endTxt = end
      ? end.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
      : "";

    return (
      <div
        className="popup-overlay"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.13)",
          zIndex: 2222,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 10,
        }}
      >
        <div
          className="popup-slot-modal"
          style={{
            width: 420,
            maxWidth: "96vw",
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 8px 30px #9d857330",
            padding: 22,
            position: "relative",
          }}
        >
          <button
            onClick={() => setCancelModal({ visible: false, rdv: null })}
            aria-label="Fermer"
            style={{
              position: "absolute",
              top: 10,
              right: 14,
              background: "none",
              border: "none",
              fontSize: 22,
              fontWeight: 700,
              color: "#b63838",
              cursor: "pointer",
            }}
          >
            ×
          </button>

          <div
            style={{
              marginBottom: 12,
              fontWeight: 800,
              fontSize: 18,
              color: "#a05c41",
            }}
          >
            Annuler ce rendez-vous ?
          </div>

          {/* Bandeau alerte */}
          <div
            style={{
              marginBottom: 14,
              padding: "10px 12px",
              borderRadius: 10,
              background: "#fee2e2",
              border: "1px solid #ef4444",
              color: "#991b1b",
              fontSize: 14,
              lineHeight: 1.35,
              fontWeight: 600,
            }}
          >
            ⚠️ <span style={{ textDecoration: "underline" }}>Important</span> :
            en cas d’annulation, merci de prévenir le professionnel (appel / SMS / e-mail)
            et, si possible, proposez un autre créneau.
          </div>

          {/* Détails RDV */}
          <div style={{ marginBottom: 8 }}>
            <strong>Date :</strong> {rdv.date || (start ? start.toLocaleDateString("fr-FR") : "-")}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Début :</strong> {startTxt}
            {endTxt ? (
              <>
                {" "}
                — <strong>Fin :</strong> {endTxt}
              </>
            ) : null}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Société :</strong>{" "}
            {rdv.societeName || rdv.nomSociete || rdv.garage || "-"}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Activité :</strong> {rdv.activity || "-"}
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong>Téléphone société :</strong>{" "}
            {rdv.societePhoneNumber || "-"}
          </div>

          {/* Boutons */}
          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <button
              onClick={() => setCancelModal({ visible: false, rdv: null })}
              style={{
                padding: "10px 16px",
                borderRadius: 10,
                border: "1px solid #ddd",
                background: "#f8f8f8",
                cursor: "pointer",
              }}
              disabled={deleting}
            >
              Fermer
            </button>
            <button
              onClick={() => reallyCancel(rdv._id)}
              style={{
                padding: "10px 16px",
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(90deg,#d63434 0%,#bb9c8d 100%)",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
                opacity: deleting ? 0.7 : 1,
              }}
              disabled={deleting}
            >
              {deleting ? "Annulation..." : "Confirmer l’annulation"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mes-rdv-container">
      <h2>Mes Rendez-vous</h2>

      {/* Boutons de bascule : À venir / Passés */}
      <div className="tab-buttons" style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button
          className={activeTab === "upcoming" ? "active" : ""}
          onClick={() => setActiveTab("upcoming")}
        >
          À venir ({upcomingRdvs.length})
        </button>
        <button
          className={activeTab === "past" ? "active" : ""}
          onClick={() => setActiveTab("past")}
        >
          Passés ({pastRdvs.length})
        </button>
      </div>

      {listToRender.length === 0 ? (
        <p className="no-rdv">
          {activeTab === "upcoming" ? "Aucun rendez-vous à venir." : "Aucun rendez-vous passé."}
        </p>
      ) : (
        <ul className="rdv-list">
          {listToRender.map((rdv) => {
            const { start, end } = getStartEnd(rdv);
            const isPassed = isPast(rdv);

            return (
              <li key={rdv._id} className="rdv-item">
                <div className="rdv-info">
                  <p>
                    <strong>Date :</strong>{" "}
                    {rdv.date || (start ? start.toLocaleDateString("fr-FR") : "-")}
                  </p>
                  <p>
                    <strong>Début :</strong>{" "}
                    {start
                      ? start.toLocaleTimeString("fr-FR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : rdv.heure || "-"}
                    &nbsp;&nbsp;
                    <strong>Fin :</strong>{" "}
                    {end
                      ? end.toLocaleTimeString("fr-FR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : ""}
                  </p>
                  <p>
                    <strong>Société :</strong>{" "}
                    {rdv.societeName || rdv.nomSociete || rdv.garage || "-"}
                  </p>
                  <p>
                    <strong>Activité :</strong> {rdv.activity || "-"}
                  </p>
                  <p>
                    <strong>Téléphone société :</strong>{" "}
                    {rdv.societePhoneNumber || "-"}
                  </p>
                </div>

                {/* Bouton d’annulation visible uniquement pour les RDVs à venir */}
                {!isPassed && activeTab === "upcoming" && (
                  <button
                    className="cancel-button"
                    onClick={() => setCancelModal({ visible: true, rdv })}
                  >
                    Annuler
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Popup de confirmation d’annulation */}
      {renderCancelPopup()}
    </div>
  );
}

export default MesRdvClient;